/**
 * Herkenning van vectorbestanden die klanten als drukbestand aanleveren:
 * EPS, AI (Illustrator), PS (PostScript) en SVG.
 *
 * Waarom dit bestaat: browsers geven voor deze bestanden een onbetrouwbaar
 * type door. Een .eps komt binnen als `''`, `application/postscript`,
 * `image/x-eps` of `application/octet-stream`, afhankelijk van het
 * besturingssysteem. Op dat type vertrouwen liet een B2B-klant met een
 * EPS-logo op de offertepagina vastlopen. Hier bepalen we het type daarom uit
 * de extensie EN de eerste bytes, op één plek, zodat browser en server
 * hetzelfde besluiten.
 *
 * Dit bestand mag geen server-only imports bevatten; het draait ook in de
 * browser.
 */

export const POSTSCRIPT_MIME = 'application/postscript';
export const SVG_MIME = 'image/svg+xml';
export const PDF_MIME = 'application/pdf';

/**
 * SVG gaat alleen via de gewone uploadroute, waar de server de inhoud ziet en
 * op scripts controleert. Een logo-SVG van meer dan 4 MB bestaat in de praktijk
 * niet; boven deze grens weigeren we liever dan ongecontroleerd op te slaan.
 */
export const MAX_SVG_UPLOAD_BYTES = 4 * 1024 * 1024;

/** Zoveel bytes lezen we voor de herkenning. Ruim genoeg voor een PDF-kop met BOM. */
export const ARTWORK_HEAD_BYTES = 4096;

export type ArtworkFormat = 'pdf' | 'ai-pdf' | 'eps' | 'ai-ps' | 'ps' | 'svg';

const VECTOR_EXTENSION = /\.(eps|epsf|epsi|ai|ps|svg)$/i;

/** Extensies die we als vector-drukbestand accepteren, voor `accept=` en teksten. */
export const VECTOR_UPLOAD_EXTENSIONS = ['.eps', '.ai', '.ps', '.svg'] as const;

/** `accept`-waarde voor bestandskiezers die drukbestanden aannemen. */
export const ARTWORK_ACCEPT_ATTRIBUTE = [
  'application/pdf', 'image/png', 'image/jpeg', 'image/webp',
  'application/postscript', 'image/svg+xml',
  '.pdf', '.png', '.jpg', '.jpeg', '.webp', ...VECTOR_UPLOAD_EXTENSIONS,
].join(',');

export function fileExtension(fileName: string | null | undefined): string {
  const match = /\.([a-z0-9]+)$/i.exec(String(fileName || '').split(/[?#]/)[0]);
  return match ? match[1].toLowerCase() : '';
}

export function isVectorArtworkFileName(fileName: string | null | undefined): boolean {
  return VECTOR_EXTENSION.test(String(fileName || '').split(/[?#]/)[0]);
}

function startsWithAscii(head: Uint8Array, text: string, offset = 0): boolean {
  if (head.length < offset + text.length) return false;
  for (let index = 0; index < text.length; index++) {
    if (head[offset + index] !== text.charCodeAt(index)) return false;
  }
  return true;
}

/** UTF-8 BOM overslaan; sommige exportprogramma's zetten die voor de kop. */
function contentStart(head: Uint8Array): number {
  return head[0] === 0xef && head[1] === 0xbb && head[2] === 0xbf ? 3 : 0;
}

/** EPS met binaire DOS-kop (Illustrator/CorelDRAW met TIFF- of WMF-voorbeeld). */
export function isDosEpsHead(head: Uint8Array): boolean {
  return head.length >= 4 && head[0] === 0xc5 && head[1] === 0xd0 && head[2] === 0xd3 && head[3] === 0xc6;
}

export function isPostScriptHead(head: Uint8Array): boolean {
  return startsWithAscii(head, '%!PS', contentStart(head)) || isDosEpsHead(head);
}

/**
 * PDF-kop. De PDF-specificatie staat toe dat `%PDF-` ergens in de eerste
 * 1024 bytes staat; Illustrator schrijft hem altijd vooraan, maar een paar
 * exporttools zetten er rommel voor.
 */
export function isPdfHead(head: Uint8Array): boolean {
  const limit = Math.min(head.length - 5, 1024);
  for (let index = 0; index <= limit; index++) {
    if (startsWithAscii(head, '%PDF-', index)) return true;
  }
  return false;
}

function decodeHeadText(head: Uint8Array): string {
  let text = '';
  const end = Math.min(head.length, ARTWORK_HEAD_BYTES);
  for (let index = 0; index < end; index++) text += String.fromCharCode(head[index]);
  return text;
}

export function isSvgHead(head: Uint8Array): boolean {
  const text = decodeHeadText(head).slice(contentStart(head)).replace(/^\s+/, '');
  if (!text.startsWith('<')) return false;
  return /<svg[\s>]/i.test(text);
}

export type ArtworkDetection = {
  format: ArtworkFormat;
  mimeType: string;
  /** Leveranciers willen PDF; dit bestand moet eerst worden omgezet. */
  needsConversion: boolean;
  /** Bestandsnaam waaronder we het bewaren (PDF-compatibele AI krijgt `.pdf` erachter). */
  storedFileName: string;
};

/**
 * Bepaalt het formaat van een vectorbestand uit naam en kopbytes.
 *
 * Geeft `null` voor bestanden die geen vectorextensie hebben (die volgen de
 * gewone typecontrole) en gooit als extensie en inhoud niet bij elkaar passen:
 * een `.eps` met PNG-bytes is geen EPS en hoort niet als drukbestand binnen te
 * komen.
 */
export function detectVectorArtwork(fileName: string, head: Uint8Array): ArtworkDetection | null {
  const ext = fileExtension(fileName);
  if (!isVectorArtworkFileName(fileName)) return null;
  const name = String(fileName || '');

  if (ext === 'ai') {
    // Illustrator bewaart standaard "PDF-compatibel": het bestand IS dan een
    // PDF. Die gaat exact als PDF verder, zonder omzetting.
    if (isPdfHead(head)) {
      return { format: 'ai-pdf', mimeType: PDF_MIME, needsConversion: false, storedFileName: `${name}.pdf` };
    }
    if (isPostScriptHead(head)) {
      return { format: 'ai-ps', mimeType: POSTSCRIPT_MIME, needsConversion: true, storedFileName: name };
    }
    throw new ArtworkFormatError('ARTWORK_CONTENT_MISMATCH');
  }
  if (ext === 'eps' || ext === 'epsf' || ext === 'epsi') {
    if (isPostScriptHead(head)) return { format: 'eps', mimeType: POSTSCRIPT_MIME, needsConversion: true, storedFileName: name };
    throw new ArtworkFormatError('ARTWORK_CONTENT_MISMATCH');
  }
  if (ext === 'ps') {
    if (isPostScriptHead(head)) return { format: 'ps', mimeType: POSTSCRIPT_MIME, needsConversion: true, storedFileName: name };
    throw new ArtworkFormatError('ARTWORK_CONTENT_MISMATCH');
  }
  if (ext === 'svg') {
    if (isSvgHead(head)) return { format: 'svg', mimeType: SVG_MIME, needsConversion: true, storedFileName: name };
    throw new ArtworkFormatError('ARTWORK_CONTENT_MISMATCH');
  }
  return null;
}

export class ArtworkFormatError extends Error {
  constructor(public readonly code: 'ARTWORK_CONTENT_MISMATCH' | 'ARTWORK_UNSAFE_SVG' | 'ARTWORK_TOO_LARGE') {
    super(code);
    this.name = 'ArtworkFormatError';
  }
}

/**
 * Controleert een SVG op actieve inhoud. We saneren niet maar weigeren: een
 * drukbestand met scripts of externe verwijzingen is geen normaal logo, en
 * stil wijzigen zou het drukwerk kunnen veranderen zonder dat iemand het weet.
 * Geeft de reden terug, of `null` als het bestand veilig is.
 */
export function svgSafetyIssue(svgText: string): string | null {
  const text = String(svgText || '');
  if (/<script[\s>/]/i.test(text)) return 'script';
  if (/<foreignObject[\s>/]/i.test(text)) return 'foreignObject';
  if (/<(iframe|embed|object|audio|video|meta|link)[\s>/]/i.test(text)) return 'embedded-element';
  if (/\son[a-z]+\s*=/i.test(text)) return 'event-handler';
  if (/(?:href|src)\s*=\s*["']?\s*(?:javascript|vbscript):/i.test(text)) return 'script-url';
  if (/data:\s*text\/html/i.test(text)) return 'html-data-url';
  // Externe bronnen: een drukbestand moet op zichzelf staan, en een externe
  // href laat het bestand bij het openen naar buiten bellen.
  if (/(?:href|src)\s*=\s*["']\s*(?:https?:)?\/\//i.test(text)) return 'external-reference';
  // Interne entiteiten zet Illustrator zelf in elke SVG-export; alleen
  // externe (SYSTEM/PUBLIC) entiteiten halen iets van buiten binnen.
  if (/<!ENTITY[^>]*\s(?:SYSTEM|PUBLIC)\s/i.test(text)) return 'external-entity';
  return null;
}

/** Heeft dit bestand (op type, naam of URL) nog een omzetting naar PDF nodig? */
export function artworkNeedsConversion(input: { mimeType?: string | null; fileName?: string | null; url?: string | null }): boolean {
  const mime = String(input.mimeType || '').split(';')[0].trim().toLowerCase();
  if (mime === POSTSCRIPT_MIME || mime === SVG_MIME || mime === 'application/illustrator' || mime === 'image/x-eps') return true;
  if (mime === PDF_MIME) return false;
  const candidates = [input.fileName, input.url].map((value) => String(value || '').split(/[?#]/)[0]);
  // `logo.ai.pdf` is de omgezette of PDF-compatibele variant: niets te doen.
  if (candidates.some((value) => /\.pdf$/i.test(value))) return false;
  return candidates.some((value) => VECTOR_EXTENSION.test(value));
}

/**
 * Leest de secties van een EPS met binaire DOS-kop.
 * Indeling (Adobe TN 5002): magic, PS-offset, PS-lengte, WMF-offset,
 * WMF-lengte, TIFF-offset, TIFF-lengte, checksum; alles little-endian.
 */
export function readDosEpsSections(bytes: Uint8Array, totalSize = bytes.length): {
  postscript: { offset: number; length: number };
  wmf: { offset: number; length: number } | null;
  tiff: { offset: number; length: number } | null;
} | null {
  if (!isDosEpsHead(bytes) || bytes.length < 30) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const read = (offset: number) => view.getUint32(offset, true);
  const section = (offset: number, length: number) =>
    offset > 0 && length > 0 && offset + length <= totalSize ? { offset, length } : null;
  const postscript = section(read(4), read(8));
  if (!postscript) return null;
  return { postscript, wmf: section(read(12), read(16)), tiff: section(read(20), read(24)) };
}

/**
 * Canoniek type voor een bestand vóór upload. Voor vectorbestanden telt de
 * inhoud, niet wat de browser zegt; voor de rest blijft het browsertype.
 */
export function canonicalUploadType(fileName: string, browserType: string, head: Uint8Array): { mimeType: string; fileName: string; detection: ArtworkDetection | null } {
  const detection = detectVectorArtwork(fileName, head);
  if (detection) return { mimeType: detection.mimeType, fileName: detection.storedFileName, detection };
  let type = String(browserType || '').split(';')[0].trim().toLowerCase();
  if (type === 'image/jpg') type = 'image/jpeg';
  if (!type) {
    const ext = fileExtension(fileName);
    type = ext === 'pdf' ? PDF_MIME : ext === 'png' ? 'image/png' : ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : ext === 'webp' ? 'image/webp' : '';
  }
  return { mimeType: type, fileName, detection: null };
}
